// utils/apiSourceUtils.ts
import { ApiSourceFormData, ApiSourceConfig, ApiSource, ResponseType, BaseSource } from '../types';

// Define valid HTTP methods
type HttpMethod = 'POST' | 'GET' | 'PUT' | 'DELETE' | 'PATCH';
type AuthType = 'none' | 'bearer' | 'basic' | 'api_key';
type AuthenticationReturn = {
    type: 'bearer';
    token: string;
} | {
    type: 'basic';
    username: string;
    password: string;
} | {
    type: 'api_key';
    key: string;
} | {
    type: 'none';
};

interface AuthConfig {
    type: 'bearer' | 'basic' | 'api_key' | 'none';
    token?: string;
    username?: string;
    password?: string;
    key?: string;
}


interface Configuration {
    endpoint?: string;
    method?: HttpMethod;
    headers?: Record<string, string>;
    authentication?: {
        type: AuthType;
        token?: string;
        username?: string;
        password?: string;
        key?: string;
    };
    performance?: Partial<ApiSourceConfig>;
    request?: {
        payload_template?: string;
    };
    response?: {
        parsing?: {
            type?: ValidResponseType;
            path?: string;
            array_path?: string;
            mapping?: Record<string, string>;
        };
    };
}

const DEFAULT_PERFORMANCE: ApiSourceConfig = {
    requests_per_minute: 60,
    retry_count: 3,
    timeout_seconds: 30,
    max_response_size_mb: 10
};

const VALID_RESPONSE_TYPES = ['json', 'xml', 'csv', 'text'] as const;
type ValidResponseType = typeof VALID_RESPONSE_TYPES[number];

function isValidResponseType(type: string | undefined): type is ValidResponseType {
    return typeof type === 'string' && VALID_RESPONSE_TYPES.includes(type as ValidResponseType);
}

function getAuthenticationFields(auth: AuthConfig): AuthenticationReturn {
    switch (auth.type) {
        case 'bearer':
            return {
                type: 'bearer',
                token: auth.token || ''
            };
        case 'basic':
            return {
                type: 'basic',
                username: auth.username || '',
                password: auth.password || ''
            };
        case 'api_key':
            return {
                type: 'api_key',
                key: auth.key || ''
            };
        case 'none':
            return {
                type: 'none'
            };
        default:
            return {
                type: 'none'
            } as const;
    }
}

type ConfigurationInput = string | Configuration | undefined;

function parseConfiguration(configuration: ConfigurationInput): Configuration {
    if (typeof configuration === 'string') {
        try {
            return JSON.parse(configuration);
        } catch (e) {
            console.error('Error parsing configuration string:', e);
            return {};
        }
    }
    return configuration || {};
}

type ConfigValue = string | number | boolean | null | undefined | Record<string, unknown>;

function getNestedValue(obj: Record<string, ConfigValue>, path: string): ConfigValue {
    return path.split('.').reduce<ConfigValue>((acc, part) => {
        if (acc && typeof acc === 'object' && !Array.isArray(acc)) {
            return (acc as Record<string, ConfigValue>)[part];
        }
        return undefined;
    }, obj);
}

type SourceConfig = {
    configuration: ConfigurationInput;
};

export function getConfigValue(source: SourceConfig, path: string): ConfigValue {
    const configuration: Configuration = parseConfiguration(source.configuration);

    if (path in DEFAULT_PERFORMANCE) {
        return getNestedValue(configuration as Record<string, ConfigValue>, `performance.${path}`) ??
            DEFAULT_PERFORMANCE[path as keyof ApiSourceConfig];
    }

    return getNestedValue(configuration as Record<string, ConfigValue>, path);
}

interface ApiSourceInput {
    name?: string;
    description?: string;
    configuration: ConfigurationInput;
}

export function transformApiSourceToFormData(apiSource: ApiSourceInput): ApiSourceFormData {
    const configuration: Configuration = parseConfiguration(apiSource.configuration);
    const rawResponseParsing = configuration?.response?.parsing || {};
    const performance = configuration?.performance || {};

    const responseType: ValidResponseType = isValidResponseType(rawResponseParsing.type)
        ? rawResponseParsing.type
        : 'json';

    const method = (configuration.method || 'POST') as HttpMethod;
    const authType = (configuration.authentication?.type || 'none') as AuthType;

    return {
        name: apiSource.name || '',
        description: apiSource.description || '',
        endpoint: configuration.endpoint || '',
        method,
        headers: configuration.headers || {},
        authentication: {
            type: authType,
            token: configuration.authentication?.token || '',
            username: configuration.authentication?.username || '',
            password: configuration.authentication?.password || '',
            key: configuration.authentication?.key || ''
        },
        payload_template: configuration.request?.payload_template || '',
        response_parsing: {
            type: responseType,
            path: rawResponseParsing.path || '',
            array_path: rawResponseParsing.array_path || '',
            mapping: rawResponseParsing.mapping || {}
        },
        config: {
            requests_per_minute: Number(performance.requests_per_minute) || DEFAULT_PERFORMANCE.requests_per_minute,
            retry_count: Number(performance.retry_count) || DEFAULT_PERFORMANCE.retry_count,
            timeout_seconds: Number(performance.timeout_seconds) || DEFAULT_PERFORMANCE.timeout_seconds,
            max_response_size_mb: Number(performance.max_response_size_mb) || DEFAULT_PERFORMANCE.max_response_size_mb
        }
    };
}

export function transformFormDataToApiPayload(formData: ApiSourceFormData): Omit<ApiSource, keyof BaseSource> {
    const responseType: ValidResponseType = isValidResponseType(formData.response_parsing.type)
        ? formData.response_parsing.type
        : 'json';

    const responseParsing: ResponseType = {
        type: responseType,
        path: formData.response_parsing.path || '',
        array_path: formData.response_parsing.array_path || '',
        mapping: formData.response_parsing.mapping
    };

    const authentication = getAuthenticationFields(formData.authentication);

    // Create a clean configuration object with properly nested fields
    const configuration = {
        endpoint: formData.endpoint,
        method: formData.method,
        headers: formData.headers || {},
        authentication,
        performance: {
            requests_per_minute: Number(formData.config.requests_per_minute),
            retry_count: Number(formData.config.retry_count),
            timeout_seconds: Number(formData.config.timeout_seconds),
            max_response_size_mb: Number(formData.config.max_response_size_mb)
        },
        request: {
            payload_template: formData.payload_template || ''
        },
        response: {
            parsing: responseParsing
        }
    };

    return {
        source_type: 'api_query',
        configuration
    };
}